import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Close, Save } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton
} from '@mui/material';
import { Box, Button, Container, Grid, Typography } from '@material-ui/core';

import { getEmaLabelSeparation, saveEmaLabelSeparationLabels } from '../../api/ema';
import Store from '../../store';
import Actions from '../../store/actions';
import AuthContext from '../../store/Auth/AuthProvider';
import { EMA_LABEL_SECTIONS } from '../../types/dtos/ema';
import type { EmaLabelSection, EmaLabelSeparationLabelsRequest } from '../../types/dtos/ema';
import HttpStatus from '../../types/enum/http_status';
import EMALabelField from './EMALabelField';
import type {
  EmaLabelCreationDataState,
  EmaLabelSeparationAuthContext,
  EmaLabelSeparationDocument,
  EmaLabelSeparationLocationState,
  EmaLabelSeparationRouteParams,
  EmaLabelSeparationStoreContext
} from './EMALabelSeparation.types';
import Styles from './EMALabelSeparation.styles';
import {
  getEmaLabelSeparationError,
  normalizeEmaLabelSections,
  validateEmaLabelSections
} from './EMALabelSeparation.utils';
import { Loading } from './EMACurationPageSeparation';

export const indexSorting =
  (ascending: boolean) =>
  (a: string[], b: string[]): number => {
    const first = a[1] ? parseInt(a[1], 10) : null;
    const second = b[1] ? parseInt(b[1], 10) : null;

    if (first === second) {
      return 0;
    }
    if (!first) {
      return 1;
    }
    if (!second) {
      return -1;
    }
    if (ascending) {
      return first < second ? -1 : 1;
    }
    return first < second ? 1 : -1;
  };

export const labelSorting =
  (labels: string[], ascending: boolean) =>
  (a: string[], b: string[]): number => {
    const first = a[0] && labels.includes(a[0]) ? labels.findIndex(value => value === a[0]) : null;
    const second = b[0] && labels.includes(b[0]) ? labels.findIndex(value => value === b[0]) : null;

    if (first === second) {
      return 0;
    }
    if (!first) {
      return 1;
    }
    if (!second) {
      return -1;
    }
    if (ascending) {
      return first < second ? -1 : 1;
    }
    return first < second ? 1 : -1;
  };

const EMACurationLabelCreation = () => {
  const { curationId } = useParams<EmaLabelSeparationRouteParams>();
  const history = useHistory();
  const location = useLocation<EmaLabelSeparationLocationState | undefined>();
  const classes = Styles();
  const { dispatch: globalDispatch } = useContext(
    Store
  ) as unknown as EmaLabelSeparationStoreContext;
  const { currentUser } = useContext(AuthContext) as unknown as EmaLabelSeparationAuthContext;
  const cachedDocument =
    location.state?.curationId === curationId ? location.state.labelSeparation : null;
  const cachedDocumentRef = useRef<EmaLabelSeparationDocument | null>(cachedDocument);
  const [dataState, setDataState] = useState<EmaLabelCreationDataState>(() => {
    const initialLabels = normalizeEmaLabelSections(cachedDocument?.labels);
    return {
      documentData: cachedDocument,
      labels: initialLabels,
      savedLabels: initialLabels,
      loading: !cachedDocument
    };
  });
  const { documentData, labels, savedLabels, loading } = dataState;
  const [isSubmitting, setSubmitting] = useState(false);
  const [openSubmitConfirmation, setOpenSubmitConfirmation] = useState(false);
  const mountedRef = useRef(true);
  const requestVersionRef = useRef(0);

  const showAlert = useCallback(
    (message: string, color?: string) => {
      globalDispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message, color }
      });
    },
    [globalDispatch]
  );

  useEffect(() => {
    mountedRef.current = true;
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;

    if (cachedDocumentRef.current) {
      return () => {
        mountedRef.current = false;
        requestVersionRef.current += 1;
      };
    }

    const loadLabels = async () => {
      if (!curationId) {
        showAlert('A curation ID is required to map label sections.');
        history.replace('/ema/curations');
        return;
      }

      const response = await getEmaLabelSeparation(curationId);
      const responseBody = response?.data?.body;
      if (response?.status !== HttpStatus.OK || !responseBody || typeof responseBody === 'string') {
        throw new Error(
          getEmaLabelSeparationError(
            responseBody,
            response?.data?.message || 'Unable to load the label section map.'
          )
        );
      }

      if (!mountedRef.current || requestVersion !== requestVersionRef.current) {
        return;
      }

      const normalizedLabels = normalizeEmaLabelSections(responseBody.labels);
      setDataState({
        documentData: {
          ...responseBody,
          labels: normalizedLabels
        },
        labels: normalizedLabels,
        savedLabels: normalizedLabels,
        loading: false
      });
    };

    loadLabels().catch(error => {
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        showAlert(error instanceof Error ? error.message : 'Unable to load the label section map.');
        history.replace('/ema/curations');
      }
    });

    return () => {
      mountedRef.current = false;
      requestVersionRef.current += 1;
    };
  }, [curationId, history, showAlert]);

  const errors = useMemo(() => validateEmaLabelSections(labels), [labels]);
  const hasMappedSection = useMemo(
    () => EMA_LABEL_SECTIONS.some(section => Boolean(labels[section])),
    [labels]
  );
  const hasChanges = useMemo(
    () => EMA_LABEL_SECTIONS.some(section => labels[section] !== savedLabels[section]),
    [labels, savedLabels]
  );
  const submitDisabled =
    loading || isSubmitting || !hasMappedSection || !hasChanges || Object.keys(errors).length > 0;

  const updateLabel = useCallback((section: EmaLabelSection, value: string) => {
    const nextValue = value || null;
    setDataState(currentState => {
      if (currentState.labels[section] === nextValue) {
        return currentState;
      }
      return {
        ...currentState,
        labels: { ...currentState.labels, [section]: nextValue }
      };
    });
  }, []);

  const requestSubmit = useCallback(() => {
    if (!submitDisabled) {
      setOpenSubmitConfirmation(true);
    }
  }, [submitDisabled]);

  const handleSubmit = useCallback(async () => {
    if (submitDisabled) {
      return;
    }
    if (!currentUser?.email || !currentUser?.['custom:user']) {
      showAlert('Curator name and email are required to save label sections.');
      return;
    }

    setOpenSubmitConfirmation(false);
    setSubmitting(true);
    const payload: EmaLabelSeparationLabelsRequest = {
      ...labels,
      curator_email: currentUser.email,
      curator_name: currentUser['custom:user']
    };
    try {
      const response = await saveEmaLabelSeparationLabels(curationId, payload);
      const responseBody = response?.data?.body;

      if (response?.status !== HttpStatus.OK || !responseBody || typeof responseBody === 'string') {
        if (mountedRef.current) {
          showAlert(
            getEmaLabelSeparationError(
              responseBody,
              response?.data?.message || 'Unable to save the label section map.'
            )
          );
          setSubmitting(false);
        }
        return;
      }

      showAlert('Document label mapped successfully', 'success');
      history.push('/ema/curations');
    } catch {
      if (mountedRef.current) {
        showAlert('Unable to save the label section map.');
        setSubmitting(false);
      }
    }
  }, [curationId, currentUser, history, labels, showAlert, submitDisabled]);

  return (
    <Box className={classes.root}>
      <Container maxWidth='lg' className={classes.container}>
        <Box className={classes.toolbar}>
          <Button
            className={classes.button}
            disabled={loading || isSubmitting}
            onClick={() => history.goBack()}
            variant='contained'>
            Go back
          </Button>
          <Button
            className={classes.button}
            disabled={submitDisabled}
            onClick={requestSubmit}
            variant='contained'>
            Submit
          </Button>
        </Box>

        <Grid container spacing={2}>
          {loading || isSubmitting ? (
            <Loading dataLoading={loading} dataSubmitting={isSubmitting} />
          ) : (
            <>
              <Grid item xs={12} lg={6}>
                <Box className={`${classes.pdfContainer} ${classes.formSection}`}>
                  {EMA_LABEL_SECTIONS.map(section => (
                    <EMALabelField
                      key={section}
                      section={section}
                      value={labels[section]}
                      error={errors[section]}
                      onChange={updateLabel}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} lg={6}>
                {documentData?.s3_url ? (
                  <Box display='flex' className={classes.pdfContainer}>
                    <iframe
                      width='100%'
                      height='100%'
                      title='EMA page-separated PDF'
                      src={documentData.s3_url}
                      className={classes.pdfEma}
                    />
                  </Box>
                ) : (
                  <Box
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    className={classes.pdfContainer}>
                    <Typography className={classes.notAvailable}>No PDF is available</Typography>
                  </Box>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Container>

      <Dialog
        open={openSubmitConfirmation}
        aria-labelledby='ema-label-map-dialog-title'
        aria-describedby='ema-label-map-dialog-description'>
        <DialogTitle id='ema-label-map-dialog-title'>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <div>Confirm submission</div>
            <IconButton aria-label='close' onClick={() => setOpenSubmitConfirmation(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='ema-label-map-dialog-description'>
            Are you sure you have mapped the page numbers accurately?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            className={classes.button}
            disabled={isSubmitting}
            onClick={() => setOpenSubmitConfirmation(false)}>
            Cancel
          </Button>
          <Button
            startIcon={<Save />}
            variant='contained'
            onClick={handleSubmit}
            disabled={isSubmitting}
            color='secondary'
            className={classes.button}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(EMACurationLabelCreation);
